import React, { useEffect, useRef, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import siteAnalyticsService, { PlatformsResponse } from '../../services/siteAnalytics.service';
import { useDateRange, RangeSelector, LoadError, fa, fmtDuration } from './useDateRange';
import { defaultDoughnutOptions, defaultLineOptions, PALETTE } from './chartConfig';
import './analytics.css';

/**
 * «اپلیکیشن در برابر وب‌سایت»
 *
 * به سه سؤالی پاسخ می‌دهد که گزارش‌های دیگر نمی‌توانند:
 *   ۱) چند نفر از اپ استفاده می‌کنند و چند نفر از سایت؟
 *   ۲) چقدر هم‌پوشانی دارند — چند کاربر هر دو را دارند؟
 *   ۳) کدام کانال بهتر تبدیل می‌شود، و روی چه دستگاه/نسخه‌ای؟
 *
 * ⚠️ تفاوت اعداد «کاربر»:
 *  - `activeUsers` (کارت‌های بالا) روی distinct userId حساب می‌شود؛ کاربری که
 *    هم اندروید و هم iOS دارد یک بار شمرده می‌شود. این عددِ درستِ «چند نفر» است.
 *  - `users` در جدولِ تفکیکِ پلتفرم per-platform است، پس جمعِ ستون می‌تواند از
 *    activeUsers بیشتر باشد. عمدی است — برای پاسخ به «چند کاربر اندروید داریم».
 */

/** رنگ ثابت برای هر کانال — در همهٔ نمودارهای این صفحه یکسان می‌ماند. */
const GROUP_COLOR: Record<string, string> = {
  app: PALETTE.success,
  web: PALETTE.primary,
};

const INSTALL_SOURCE_FA: Record<string, string> = {
  cafebazaar: 'کافه‌بازار',
  myket: 'مایکت',
  googleplay: 'گوگل‌پلی',
  appstore: 'اپ‌استور',
  direct: 'نصب مستقیم',
};

const SiteAnalyticsPlatforms: React.FC = () => {
  const dr = useDateRange('30d');
  const [data, setData] = useState<PlatformsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const reqIdRef = useRef(0);

  const load = async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setErr(null);
    try {
      const r = await siteAnalyticsService.getPlatforms(dr.range);
      // پاسخ کند شده‌ای که بعد از یک بازهٔ جدیدتر می‌رسد نباید UI را عقب ببرد.
      if (reqId !== reqIdRef.current) return;
      setData(r);
    } catch (e: any) {
      if (reqId !== reqIdRef.current) return;
      setErr(e?.message || 'خطا در دریافت داده');
    } finally {
      if (reqId === reqIdRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dr.range.from, dr.range.to, dr.refreshKey]);

  const app = data?.summary.find((s) => s.group === 'app');
  const web = data?.summary.find((s) => s.group === 'web');

  const trendChart = data
    ? {
        labels: data.trend.map((r) => r.date),
        datasets: [
          {
            label: 'اپلیکیشن',
            data: data.trend.map((r) => r.app),
            borderColor: GROUP_COLOR.app,
            backgroundColor: PALETTE.successLight,
            fill: true,
            tension: 0.3,
          },
          {
            label: 'وب‌سایت',
            data: data.trend.map((r) => r.web),
            borderColor: GROUP_COLOR.web,
            backgroundColor: PALETTE.primaryLight,
            fill: true,
            tension: 0.3,
          },
        ],
      }
    : null;

  const splitChart = data
    ? {
        labels: ['فقط اپ', 'فقط وب', 'هر دو'],
        datasets: [
          {
            data: [data.userSplit.appOnly, data.userSplit.webOnly, data.userSplit.both],
            backgroundColor: [GROUP_COLOR.app, GROUP_COLOR.web, PALETTE.purple],
          },
        ],
      }
    : null;

  const versionChart =
    data && data.appVersions.length > 0
      ? {
          labels: data.appVersions.slice(0, 10).map((v) => `${v.version} (${v.label})`),
          datasets: [
            {
              label: 'سشن',
              data: data.appVersions.slice(0, 10).map((v) => v.sessions),
              backgroundColor: PALETTE.teal,
            },
          ],
        }
      : null;

  const hasAppData = !!app && app.sessions > 0;

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <h2>اپلیکیشن در برابر وب‌سایت</h2>
        <div className="analytics-controls">
          <RangeSelector {...dr} />
          <button onClick={dr.refresh} disabled={loading}>
            {loading ? 'در حال بارگذاری…' : 'بروزرسانی'}
          </button>
        </div>
      </div>

      {err && <LoadError message={err} onRetry={load} />}
      {loading && !data && <div className="analytics-loading">در حال بارگذاری…</div>}

      {data && (
        <>
          {/* بدون دادهٔ اپ، صفحه خالی به نظر می‌رسد و کاربرِ پنل فکر می‌کند خراب
              است — علت واقعی را صریح می‌گوییم. */}
          {!hasAppData && (
            <div className="analytics-section" style={{ marginBottom: 16 }}>
              <div className="analytics-empty">
                هنوز هیچ سشنی از اپلیکیشن ثبت نشده است. داده از لحظه‌ای شروع می‌شود که
                نسخهٔ دارای ماژول آنالیتیکس روی دستگاه کاربران نصب شود.
              </div>
            </div>
          )}

          {/* ── کاربران فعال: پاسخ مستقیم به «چند نفر از اپ استفاده می‌کنند» ── */}
          <div className="analytics-kpi-grid">
            <div className="analytics-kpi-card green">
              <div className="accent" />
              <div className="label">کاربران فعال اپ (۳۰ روز)</div>
              <div className="value">{fa(data.activeUsers.app?.d30 ?? 0)}</div>
              <span className="change flat">
                امروز {fa(data.activeUsers.app?.d1 ?? 0)} · هفته {fa(data.activeUsers.app?.d7 ?? 0)}
              </span>
            </div>
            <div className="analytics-kpi-card">
              <div className="accent" />
              <div className="label">کاربران فعال وب (۳۰ روز)</div>
              <div className="value">{fa(data.activeUsers.web?.d30 ?? 0)}</div>
              <span className="change flat">
                امروز {fa(data.activeUsers.web?.d1 ?? 0)} · هفته {fa(data.activeUsers.web?.d7 ?? 0)}
              </span>
            </div>
            <div className="analytics-kpi-card purple">
              <div className="accent" />
              <div className="label">نفوذ اپلیکیشن</div>
              <div className="value">{fa(data.userSplit.appAdoptionPct)}٪</div>
              <span className="change flat">
                از {fa(data.userSplit.known)} کاربر شناسایی‌شده
              </span>
            </div>
            <div className="analytics-kpi-card teal">
              <div className="accent" />
              <div className="label">نصب‌های فعال (۳۰ روز)</div>
              <div className="value">
                {fa(
                  data.devices
                    .filter((d) => d.platform === 'android' || d.platform === 'ios')
                    .reduce((s, d) => s + d.activeLast30d, 0)
                )}
              </div>
              <span className="change flat">
                مجموع دستگاه‌های ثبت‌شده{' '}
                {fa(
                  data.devices
                    .filter((d) => d.platform === 'android' || d.platform === 'ios')
                    .reduce((s, d) => s + d.devices, 0)
                )}
              </span>
            </div>
          </div>

          {/* ── مقایسهٔ کیفیت تعامل ── */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>مقایسهٔ کانال‌ها در بازهٔ انتخابی</h3>
            </div>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>کانال</th>
                  <th>سهم سشن</th>
                  <th>سشن</th>
                  <th>بازدیدکننده</th>
                  <th>بازدید صفحه</th>
                  <th>صفحه/سشن</th>
                  <th>میانگین مدت</th>
                  <th>Bounce</th>
                  <th>ثبت‌نام</th>
                  <th>خرید</th>
                  <th>نرخ خرید</th>
                </tr>
              </thead>
              <tbody>
                {data.summary.map((row) => {
                  const share = data.shares.find((s) => s.group === row.group);
                  return (
                    <tr key={row.group}>
                      <td>
                        <span
                          style={{
                            background: GROUP_COLOR[row.group],
                            color: '#fff',
                            padding: '2px 10px',
                            borderRadius: 12,
                            fontSize: 12,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {row.label}
                        </span>
                      </td>
                      <td>{fa(share?.sessionSharePct ?? 0)}٪</td>
                      <td>{fa(row.sessions)}</td>
                      <td>{fa(row.visitors)}</td>
                      <td>{fa(row.pageViews)}</td>
                      <td>{fa(row.pagesPerSession)}</td>
                      <td>{fmtDuration(row.avgSessionDurationSec)}</td>
                      <td>{fa(row.bounceRate)}٪</td>
                      <td>{fa(row.signups)}</td>
                      <td>{fa(row.purchases)}</td>
                      <td>{fa(row.purchaseRatePct)}٪</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>روند روزانهٔ سشن</h3>
              </div>
              <div className="analytics-chart-container">
                {trendChart && trendChart.labels.length > 0 ? (
                  <Line data={trendChart} options={defaultLineOptions} />
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>هم‌پوشانی کاربران</h3>
              </div>
              <div className="analytics-chart-container">
                {splitChart && data.userSplit.known > 0 ? (
                  <Doughnut data={splitChart} options={defaultDoughnutOptions} />
                ) : (
                  <div className="analytics-empty">داده‌ای نیست</div>
                )}
              </div>
            </div>
          </div>

          {/* ── تفکیک ریزدانه: اندروید / iOS / وب جدا ── */}
          <div className="analytics-section">
            <div className="analytics-section-header">
              <h3>تفکیک پلتفرم</h3>
            </div>
            <table className="analytics-table">
              <thead>
                <tr>
                  <th>پلتفرم</th>
                  <th>سشن</th>
                  <th>بازدیدکننده</th>
                  <th>کاربر لاگین‌کرده</th>
                  <th>بازدید صفحه</th>
                  <th>رویداد</th>
                  <th>میانگین مدت</th>
                  <th>Bounce</th>
                  <th>خرید</th>
                </tr>
              </thead>
              <tbody>
                {data.byPlatform.length === 0 && (
                  <tr>
                    <td colSpan={9}>
                      <div className="analytics-empty">داده‌ای نیست</div>
                    </td>
                  </tr>
                )}
                {data.byPlatform.map((row) => (
                  <tr key={row.platform}>
                    <td>{row.label}</td>
                    <td>{fa(row.sessions)}</td>
                    <td>{fa(row.visitors)}</td>
                    <td>{fa(row.users)}</td>
                    <td>{fa(row.pageViews)}</td>
                    <td>{fa(row.events)}</td>
                    <td>{fmtDuration(row.avgSessionDurationSec)}</td>
                    <td>{fa(row.bounceRate)}٪</td>
                    <td>{fa(row.purchases)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── بخش‌های مخصوص اپ ── */}
          {hasAppData && (
            <>
              <div className="analytics-grid-2col">
                <div className="analytics-section">
                  <div className="analytics-section-header">
                    <h3>نسخه‌های نصب‌شدهٔ اپ</h3>
                  </div>
                  <div className="analytics-chart-container">
                    {versionChart ? (
                      <Bar
                        data={versionChart}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y' as const,
                          plugins: { legend: { display: false } },
                          scales: { x: { beginAtZero: true } },
                        }}
                      />
                    ) : (
                      <div className="analytics-empty">داده‌ای نیست</div>
                    )}
                  </div>
                </div>
                <div className="analytics-section">
                  <div className="analytics-section-header">
                    <h3>منبع نصب</h3>
                  </div>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>فروشگاه</th>
                        <th>سشن</th>
                        <th>کاربر</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.installSources.length === 0 && (
                        <tr>
                          <td colSpan={3}>
                            <div className="analytics-empty">داده‌ای نیست</div>
                          </td>
                        </tr>
                      )}
                      {data.installSources.map((r) => (
                        <tr key={r.value}>
                          <td>{INSTALL_SOURCE_FA[r.value] || r.value}</td>
                          <td>{fa(r.sessions)}</td>
                          <td>{fa(r.users)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="analytics-grid-2col">
                <div className="analytics-section">
                  <div className="analytics-section-header">
                    <h3>پرکاربردترین دستگاه‌ها</h3>
                  </div>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>مدل</th>
                        <th>سشن</th>
                        <th>بازدیدکننده</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.appDevices.map((r) => (
                        <tr key={r.value}>
                          <td>{r.value}</td>
                          <td>{fa(r.sessions)}</td>
                          <td>{fa(r.visitors)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="analytics-section">
                  <div className="analytics-section-header">
                    <h3>نسخهٔ سیستم‌عامل</h3>
                  </div>
                  <table className="analytics-table">
                    <thead>
                      <tr>
                        <th>سیستم‌عامل</th>
                        <th>سشن</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.appOsVersions.map((r) => (
                        <tr key={`${r.os}-${r.version}`}>
                          <td>{r.version || r.os || '(نامشخص)'}</td>
                          <td>{fa(r.sessions)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* ── صفحات پربازدید، کنار هم برای مقایسهٔ رفتار ── */}
          <div className="analytics-grid-2col">
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>پربازدیدترین صفحات اپ</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>مسیر</th>
                    <th>بازدید</th>
                    <th>بازدیدکننده</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.topScreens.app || []).length === 0 && (
                    <tr>
                      <td colSpan={3}>
                        <div className="analytics-empty">داده‌ای نیست</div>
                      </td>
                    </tr>
                  )}
                  {(data.topScreens.app || []).map((r) => (
                    <tr key={r.path}>
                      <td>{r.path}</td>
                      <td>{fa(r.pageViews)}</td>
                      <td>{fa(r.visitors)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="analytics-section">
              <div className="analytics-section-header">
                <h3>پربازدیدترین صفحات وب</h3>
              </div>
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>مسیر</th>
                    <th>بازدید</th>
                    <th>بازدیدکننده</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.topScreens.web || []).map((r) => (
                    <tr key={r.path}>
                      <td>{r.path}</td>
                      <td>{fa(r.pageViews)}</td>
                      <td>{fa(r.visitors)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SiteAnalyticsPlatforms;
